'use client';

import { StandardLoader } from '@/components/ui/loaders';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';

import { computeSunTimes } from '../getSun';
import { useApiTypes, usePlants, useSun } from '../store';
import { insertPlantOrderMutation, insertUserMutation, updatePlantOrderMutation } from './mutations';
import {
    getApiTypesQuery,
    getPlantOrderQuery,
    getPlantsQuery,
    getPowerSeriesQuery,
    getScintillaPlantsQuery,
    getUserQuery,
} from './queries';

// Coordinate dell'area impianti (Ancona), per la stima giorno / notte.
const PLANT_LAT = 43.6;
const PLANT_LNG = 13.5;

const date = new Date(new Date().setHours(0, 0, 0, 0));

const Initialization = ({ children }) => {
    const { data: session, status } = useSession();
    const skip = status === 'unauthenticated';

    // Metadati: leggeri una volta indicizzata "measure". La griglia si mostra
    // appena questi sono pronti, senza aspettare le serie di potenza.
    const { data: plants } = useQuery(getPlantsQuery, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        variables: { date },
        skip,
    });
    const { data: scintillaPlants } = useQuery(getScintillaPlantsQuery, { skip });
    const { data: apiTypes } = useQuery(getApiTypesQuery, { skip });
    const { data: users } = useQuery(getUserQuery, { skip, fetchPolicy: 'no-cache' });
    const { data: plantOrder } = useQuery(getPlantOrderQuery, { skip, fetchPolicy: 'no-cache' });

    // Serie sparkline: caricata in lazy DOPO i metadati e solo per gli impianti
    // effettivamente presenti. L'aggregazione per fascia oraria e' fatta dal DB.
    const [getPowerSeries, { data: powerSeries }] = useLazyQuery(getPowerSeriesQuery, {
        fetchPolicy: 'cache-and-network',
    });

    const [insertPlantOrder] = useMutation(insertPlantOrderMutation, { refetchQueries: ['getPlantOrder'] });
    const [updatePlantOrder] = useMutation(updatePlantOrderMutation, { refetchQueries: ['getPlantOrder'] });
    const [insertUser] = useMutation(insertUserMutation, {
        refetchQueries: ['getUser'],
        variables: { email: session?.data.email },
    });

    const { setPlants, setPlantOrder, setScintillaPlants, setPowerMeasuresData, setPowerMeasuresLoading } = usePlants();
    const { setSunrise, setSunset } = useSun();
    const { setApiTypes } = useApiTypes();

    const [everythingIsLoaded, setEverythingIsLoaded] = useState(skip);

    // Alba / tramonto calcolati localmente: nessuna dipendenza da API esterne
    // nel percorso di caricamento.
    useEffect(() => {
        const { sunrise, sunset } = computeSunTimes(new Date(), PLANT_LAT, PLANT_LNG);
        setSunrise(sunrise);
        setSunset(sunset);
    }, []);

    // Bootstrap (utente + ordine impianti) e popolamento dello store.
    useEffect(() => {
        if (![users, plants, apiTypes, scintillaPlants, plantOrder].every(Boolean)) return;

        if (users.user.length === 0) {
            insertUser();
            return;
        }
        if (plantOrder.plantOrder.length === 0) {
            insertPlantOrder({
                variables: { email: session?.data.email, order: plants.plant.map((plant) => plant.id) },
            });
            return;
        }

        const order = plantOrder.plantOrder[0].order;
        const missingPlantIds = plants.plant.map((plant) => plant.id).filter((id) => !order.includes(id));
        if (missingPlantIds.length > 0) {
            updatePlantOrder({ variables: { email: session.data.email, order: order.concat(missingPlantIds) } });
        }

        setPlants(plants.plant);
        setPlantOrder(order);
        setScintillaPlants(scintillaPlants.plant);
        setApiTypes(apiTypes.apiType);
        setEverythingIsLoaded(true);
    }, [users, plants, apiTypes, scintillaPlants, plantOrder]);

    // Una volta noti gli impianti, chiedo le serie di potenza al DB.
    useEffect(() => {
        if (!plants?.plant?.length) return;
        setPowerMeasuresLoading(true);
        getPowerSeries({ variables: { plantIds: `{${plants.plant.map((plant) => plant.id).join(',')}}` } });
    }, [plants]);

    // Le righe { plantId, hour, power } arrivano gia' ordinate per (plantId,
    // hour): raggruppo nella forma attesa da PlantCard / PlantModalContent.
    useEffect(() => {
        if (!powerSeries) return;
        const grouped = {};
        for (const row of powerSeries.powerSeries) {
            (grouped[row.plantId] ||= []).push({ date: String(row.hour), Power: row.power });
        }
        setPowerMeasuresData(Object.entries(grouped).map(([plantId, data]) => ({ plantId: parseInt(plantId), data })));
        setPowerMeasuresLoading(false);
    }, [powerSeries]);

    return everythingIsLoaded ? children : <StandardLoader />;
};

export default Initialization;
